import routes from '@config/routes';
import instance from '@instance';
import { addQueryParam } from '@utils/add-query-param';
import { getCanonicalLeagueTeamSlug } from '@utils/navigation/league-public-paths';
import { getConcertImagePath } from './config';
import { formatLeagueSlug, getVenueImages } from './getVenueImages';
import { WORLD_CUP_TEAM_SLUG_MAP } from './constants/worldCupTeamSlugs';
import { guardCategoryEventsForTeam } from './guard-team-events';
import {
  getEuropeanSoccerTeamByCategory,
  getEuropeanSoccerTeamsByCategory,
  isRelevantUefaEventName,
} from './getTeamsByLeague/europeanSoccer';

interface Props {
  categoryName: string;
  serverSide?: boolean;
  page?: number;
  startDate?: string;
  endDate?: string;
  teamSlug?: string;
  onlyHomeGames?: boolean;
  opponentId?: number;
  perPage?: number;
  orderBy?: string;
  withVenueImages?: boolean;
  concerts?: boolean;
}

const DEFAULT_VENUE_IMAGE_PATH = '/default/600x400-generic-event.jpg';

const EUROPEAN_CATEGORY_ALIASES: Record<string, string[]> = {
  'english-premier-league': ['english-premier-league'],
  'la-liga': ['la-liga', 'spanish-primera-division', 'soccer'],
  'italian-serie-a': ['italian-serie-a'],
  bundesliga: ['bundesliga'],
  'ligue-1': ['ligue-1'],
  'uefa-soccer': ['uefa-champions-league', 'uefa-europa-conference-league'],
  'champions-league': ['uefa-champions-league'],
  'champions-league-final': ['uefa-champions-league'],
  'europa-league': [],
  'europa-league-final': [],
  'conference-league': ['uefa-europa-conference-league'],
  'conference-league-final': ['uefa-europa-conference-league'],
};

const isEuropeanCategory = (categoryName: string) =>
  Object.prototype.hasOwnProperty.call(EUROPEAN_CATEGORY_ALIASES, categoryName);

const UEFA_CATEGORY_NAMES = new Set([
  'uefa-soccer',
  'champions-league',
  'champions-league-final',
  'europa-league',
  'europa-league-final',
  'conference-league',
  'conference-league-final',
]);

const isUefaCategory = (categoryName: string) =>
  UEFA_CATEGORY_NAMES.has(categoryName);

const getEuropeanSoccerEvents = async ({
  categoryName,
  teamSlug,
  page,
  startDate,
  endDate,
  onlyHomeGames,
  opponentId,
  orderBy,
}: Props) => {
  const team = teamSlug
    ? getEuropeanSoccerTeamByCategory(categoryName, teamSlug)
    : undefined;
  const leagueTeamIds = getEuropeanSoccerTeamsByCategory(categoryName)
    .map(({ teamId }) => teamId)
    .filter((teamId): teamId is number => teamId !== undefined);
  if (teamSlug && !team) return null;
  if (!isEuropeanCategory(categoryName)) return null;

  const response = await instance.api('/te/proxy', {
    method: 'POST',
    body: JSON.stringify({
      performerId: team?.teamId,
      league: team?.teamId ? undefined : categoryName,
      page,
      startDate,
      endDate,
      home: onlyHomeGames,
      opponentId,
      orderBy,
      isPagination: !team?.teamId,
    }),
  });

  const data = await response.json();
  const categoryAliases = team?.ticketEvolutionCategorySlug
    ? [team.ticketEvolutionCategorySlug]
    : (EUROPEAN_CATEGORY_ALIASES[categoryName] ?? [categoryName]);
  const eventNameIncludes = team?.eventNameIncludes?.toLowerCase();
  const events = (data.events ?? []).filter(
    (event: {
      name?: string;
      category?: { slug?: string };
      performances?: { performer?: { id?: number } }[];
    }) =>
      categoryAliases.includes(event.category?.slug ?? '') &&
      (!eventNameIncludes ||
        event.name?.toLowerCase().includes(eventNameIncludes)) &&
      (!isUefaCategory(categoryName) || isRelevantUefaEventName(event.name)) &&
      (team?.teamId
        ? event.performances?.some(
            ({ performer }) => performer?.id === team.teamId
          )
        : leagueTeamIds.length === 0 ||
          event.performances?.some(({ performer }) =>
            leagueTeamIds.includes(performer?.id ?? 0)
          ))
  );

  return {
    ...data,
    events,
    total_entries: isUefaCategory(categoryName)
      ? events.length
      : (data.total_entries ?? events.length),
  };
};

const getEventsClientSide = async ({
  page = 1,
  categoryName,
  startDate,
  endDate,
  teamSlug,
  onlyHomeGames,
  opponentId,
  perPage,
  orderBy,
  serverSide = false,
}: Props) => {
  const europeanSoccerEvents = await getEuropeanSoccerEvents({
    categoryName,
    teamSlug,
    page,
    startDate,
    endDate,
    onlyHomeGames,
    opponentId,
    orderBy,
  });

  if (europeanSoccerEvents) return europeanSoccerEvents;

  const resolvedSlug =
    teamSlug && categoryName === 'world-cup'
      ? (WORLD_CUP_TEAM_SLUG_MAP[teamSlug.toLowerCase()]?.slug ?? teamSlug)
      : teamSlug && categoryName
        ? getCanonicalLeagueTeamSlug(categoryName, teamSlug)
        : teamSlug;

  const data = {
    page,
    league: categoryName,
    startDate: startDate ? new Date(startDate).toISOString() : undefined,
    endDate: endDate ? new Date(endDate).toISOString() : undefined,
    teamSlug: resolvedSlug,
    onlyHomeGames,
    opponentId,
    perPage,
    orderBy,
  };

  const response = await instance.server(
    `${routes.eventsCategory}?${addQueryParam(data)}`,
    {
      method: 'GET',
      ...(serverSide
        ? { next: { revalidate: 3600 } }
        : { cache: 'no-store' as RequestCache }),
    }
  );

  const formattedData = await response.json();

  // BE sometimes falls back to the full category inventory when a teamSlug
  // cannot be resolved. Never surface that on a team page.
  return guardCategoryEventsForTeam(formattedData, resolvedSlug);
};

export const getEventsByCategory = async ({
  withVenueImages,
  concerts,
  ...props
}: Props) => {
  const eventsResponse = await getEventsClientSide(props);

  if (concerts) {
    const formattedEvents = eventsResponse.events.map((event) => {
      return {
        ...event,
        venue: {
          ...event?.venue,
          image: getConcertImagePath(event.id ?? event.name),
        },
      };
    });

    return { ...eventsResponse, events: formattedEvents };
  }

  if (!withVenueImages) return eventsResponse;

  if (!eventsResponse?.events?.length) return { events: [], total_entries: 0 };

  // Use the requested categoryName as the canonical league, not each event's
  // own category slug — TicketEvolution sometimes tags live events under a
  // legacy/aliased slug (e.g. La Liga events come back as
  // "spanish-primera-division") that won't match our league-keyed venue
  // image data, silently falling back to the ungated generic default.
  const canonicalLeague = formatLeagueSlug(props.categoryName);
  const venueImages = await getVenueImages(
    eventsResponse.events,
    canonicalLeague
  );
  const venueImageByKey = new Map(
    venueImages.map((item) => [
      `${item.venueId}:${item.league}`,
      item.imagePath,
    ])
  );

  const formattedEvents = eventsResponse.events
    .map((event) => {
      const venueImage = venueImageByKey.get(
        `${event?.venue?.id}:${canonicalLeague}`
      );
      return {
        ...event,
        venue: {
          ...event?.venue,
          image:
            typeof venueImage === 'string' &&
            venueImage.includes(DEFAULT_VENUE_IMAGE_PATH)
              ? undefined
              : venueImage,
        },
      };
    })
    .filter(
      (ev) =>
        !ev.name
          .toLowerCase()
          .includes('(includes tickets to all regular season home games)')
    );

  return {
    ...eventsResponse,
    events: formattedEvents,
  };
};
